import React from 'react';
import PropTypes from 'prop-types';
import Separator from 'tailwind-components/Separator/Separator';
import ContentLibraryCategoryTabs from './ContentLibraryCategoryTabs';
import ContentLibraryFilterSelect from './ContentLibraryFilterSelect';
import ContentLibraryAssetGrid from './ContentLibraryAssetGrid';
import { ALL, NO_CARRIER } from './contentLibraryUtils';

export default function ContentLibraryMainPanel({
  categories,
  selectedCategory,
  onCategoryChange,
  carrierList,
  selectedCarrier,
  onCarrierChange,
  allTypeLabels,
  selectedType,
  onTypeChange,
  searchFieldId,
  searchQuery,
  onSearchQueryChange,
  filteredAssets,
  selectedAsset,
  onSelectAsset
}) {
  return (
    <div className='flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-hidden rounded-t-xl bg-white p-4'>
      <h2 className='m-0 shrink-0 bg-white pt-2 pb-0.5 text-center text-2xl font-semibold text-gray-500 md:text-3xl'>
        Content Library
      </h2>

      <div className='my-1.5 w-full shrink-0 px-0'>
        <Separator variant='solid' />
      </div>

      <div data-tour='content-library-tabs'>
        <ContentLibraryCategoryTabs
          categories={categories}
          activeCategory={selectedCategory}
          onCategoryChange={onCategoryChange}
          className='flex flex-wrap items-center justify-center'
        />
      </div>

      <div className='mb-0 mt-1.5 w-full shrink-0 px-0'>
        <Separator variant='solid' />
      </div>

      <div data-tour='content-library-filters' className='mb-4 flex w-full min-w-0 flex-col gap-3 pt-2 md:flex-row md:items-end md:justify-between md:gap-4'>
        <div className='flex min-w-0 w-full shrink-0 items-end gap-2 sm:gap-3 md:w-auto md:flex-none'>
          <div className='min-w-0 flex-1 md:w-[200px] md:max-w-[200px] md:shrink-0'>
            <ContentLibraryFilterSelect
              label='Carrier'
              value={selectedCarrier}
              onChange={onCarrierChange}
            >
              {carrierList.map(c => {
                if (c === ALL) {
                  return (
                    <option key='all' value={ALL}>
                      All carriers
                    </option>
                  );
                }
                if (c === NO_CARRIER) {
                  return (
                    <option key='no-carrier' value={NO_CARRIER}>
                      No carrier
                    </option>
                  );
                }
                return (
                  <option key={c} value={c}>
                    {c}
                  </option>
                );
              })}
            </ContentLibraryFilterSelect>
          </div>
          <div className='min-w-0 flex-1 md:w-[200px] md:max-w-[200px] md:shrink-0'>
            <ContentLibraryFilterSelect
              label='Material type'
              value={selectedType}
              onChange={onTypeChange}
            >
              {allTypeLabels.map(t => (
                <option key={t} value={t}>
                  {t === ALL ? 'All types' : t}
                </option>
              ))}
            </ContentLibraryFilterSelect>
          </div>
        </div>

        <div className='relative w-full min-w-0 md:ml-auto md:max-w-md md:min-w-48 md:flex-1'>
          <label
            htmlFor={searchFieldId}
            className='pointer-events-none absolute left-3 top-0 z-1 -translate-y-1/2 bg-white px-1 text-xs font-medium text-gray-600'
          >
            Search
          </label>
          <input
            id={searchFieldId}
            type='search'
            placeholder='Type in to Search...'
            value={searchQuery}
            onChange={e => onSearchQueryChange(e.target.value)}
            className='box-border min-h-11 w-full rounded border border-gray-400 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          />
        </div>
      </div>

      <div data-tour='content-library-grid' className='min-h-0 flex-1 overflow-y-auto show-scrollbar pr-1'>
        <ContentLibraryAssetGrid
          assets={filteredAssets}
          selectedAsset={selectedAsset}
          onSelectAsset={onSelectAsset}
        />
      </div>
    </div>
  );
}

ContentLibraryMainPanel.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedCategory: PropTypes.string.isRequired,
  onCategoryChange: PropTypes.func.isRequired,
  carrierList: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedCarrier: PropTypes.string.isRequired,
  onCarrierChange: PropTypes.func.isRequired,
  allTypeLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedType: PropTypes.string.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  searchFieldId: PropTypes.string.isRequired,
  searchQuery: PropTypes.string.isRequired,
  onSearchQueryChange: PropTypes.func.isRequired,
  filteredAssets: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedAsset: PropTypes.object,
  onSelectAsset: PropTypes.func.isRequired
};

ContentLibraryMainPanel.defaultProps = {
  selectedAsset: null
};
